/**
 * Item Service
 * Handles API calls for menu item management
 */

import { api } from '@/lib/axios';
import type {
  Item,
  CreateItemRequest,
  UpdateItemRequest,
  ItemFilters,
  CatalogResponse,
  CatalogImportResponse,
} from '@/types/item';

export const itemService = {
  /**
   * Get all items
   */
  async getItems(filters?: ItemFilters): Promise<Item[]> {
    const params = new URLSearchParams();

    if (filters?.category) {
      params.append('category', filters.category);
    }

    if (filters?.active !== undefined) {
      params.append('active', filters.active.toString());
    }

    const response = await api.get<Item[]>(
      `/v1/admin/items${params.toString() ? `?${params.toString()}` : ''}`
    );
    return response.data;
  },

  /**
   * Get a single item by ID or remote_item_id
   */
  async getItem(id: string, lookup?: 'remote'): Promise<Item> {
    const params = lookup ? `?lookup=${lookup}` : '';
    const response = await api.get<Item>(`/v1/admin/items/${id}${params}`);
    return response.data;
  },

  /**
   * Create a new item
   */
  async createItem(data: CreateItemRequest): Promise<Item> {
    const response = await api.post<Item>('/v1/admin/items', data);
    return response.data;
  },

  /**
   * Update an existing item
   */
  async updateItem(id: string, data: UpdateItemRequest): Promise<Item> {
    const response = await api.patch<Item>(`/v1/admin/items/${id}`, data);
    return response.data;
  },

  /**
   * Delete an item
   */
  async deleteItem(id: string): Promise<void> {
    await api.delete(`/v1/admin/items/${id}`);
  },

  /**
   * TPV catalog products that are not in Kombu yet
   */
  async getCatalog(): Promise<CatalogResponse> {
    const response = await api.get<CatalogResponse>('/v1/admin/items/catalog');
    return response.data;
  },

  /**
   * Create dishes from TPV catalog ids (data is read from the TPV by the API)
   */
  async importFromCatalog(remoteItemIds: string[]): Promise<CatalogImportResponse> {
    const response = await api.post<CatalogImportResponse>('/v1/admin/items/import', {
      remote_item_ids: remoteItemIds,
    });
    return response.data;
  },

  /**
   * Toggle item active status
   */
  async toggleActive(id: string, active: boolean): Promise<Item> {
    const response = await api.patch<Item>(`/v1/admin/items/${id}`, {
      item: { active }
    });
    return response.data;
  },
};
